// TASK-K2T-8 — pure helpers + the workspace-badge fetch for the Integrity tab.
//
// The tab renders the server-assembled IntegritySnapshot (GET /api/autotrader/{id}/integrity): the
// invariant checks, the ledger×marks×targets drift table, the advisory rebalance ticket, cost
// accounting, cadence, and the Shishito stop ladder. ALL verdicts and aggregates are SERVER-computed
// (one snapshot so every surface shows identical numbers) — this module only formats and orders for
// display; it deliberately re-implements no policy (the rollup arrives as `snapshot.overall`). Wire
// statuses are the server enum names: 'NotApplicable' | 'Ok' | 'Warn' | 'Error'.
import api from './api.js';

/** Display label for a wire status. */
export function statusLabel(status) {
  switch (status) {
    case 'Ok': return 'OK';
    case 'Warn': return 'WARN';
    case 'Error': return 'ERROR';
    default: return 'N/A';
  }
}

/** CSS tone suffix for a wire status (`it-ok` / `it-warn` / `it-error` / `it-na`). */
export function statusTone(status) {
  switch (status) {
    case 'Ok': return 'ok';
    case 'Warn': return 'warn';
    case 'Error': return 'error';
    default: return 'na';
  }
}

/** Canonical underscore symbol → display form (BTC_USD → BTC-USD). */
export function displaySymbol(symbol) {
  return String(symbol ?? '').replace('_', '-');
}

/** Drift-table order: biggest positions first (|value| desc); unvalued rows last, alphabetically. Pure — no mutation. */
export function sortBook(rows) {
  return [...(rows ?? [])].sort((a, b) => {
    const av = a.value == null ? null : Math.abs(a.value);
    const bv = b.value == null ? null : Math.abs(b.value);
    if (av != null && bv != null && av !== bv) return bv - av;
    if (av != null && bv == null) return -1;
    if (av == null && bv != null) return 1;
    return String(a.symbol).localeCompare(String(b.symbol));
  });
}

/** A weight as a percentage; null/undefined renders the honest dash, never a fake zero. */
export function fmtWeight(w, dp = 1) {
  return w == null ? '—' : `${(w * 100).toFixed(dp)}%`;
}

/** A drift with an EXPLICIT sign (design rule: never encode direction by color alone). U+2212 minus. */
export function fmtDrift(w, dp = 1) {
  if (w == null) return '—';
  const pct = (Math.abs(w) * 100).toFixed(dp);
  if (w > 0) return `+${pct}%`;
  if (w < 0) return `−${pct}%`;
  return `${pct}%`;
}

/**
 * The workspace tab-dot fetch: resolves to the snapshot's server-computed `overall` status for
 * (strategyId, orgId), or null on any failure / missing data (no dot — never a fake green). The
 * CALLER owns refresh keying and stale-response discipline (StrategyWorkspace re-fetches on its
 * strategy|org|latest-bar key, same cadence as the Errors/Alerts badges).
 */
export async function fetchIntegrityOverall(strategyId, orgId) {
  if (!strategyId) return null;
  try {
    // TASK-K2T-15 (AC 12): the LIGHTWEIGHT summary — the dot must not pay for the full snapshot.
    const s = await api.getIntegritySummary(strategyId, orgId);
    return s?.overall ?? null;
  } catch {
    // Half-deploy fallback (deploy-host-coverage lesson 2026-08-20): the SPA ships on K2Capital,
    // the summary route on K2AutoTrader — if the API half is older (or rolled back), fall back to
    // the long-deployed full endpoint rather than silently dropping every warning dot.
    try {
      const full = await api.getStrategyIntegrity(strategyId, orgId);
      return full?.overall ?? null;
    } catch {
      return null; // no dot — never a fake green.
    }
  }
}
